import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import GoogleSignInButton from '../../components/GoogleSignInButton/GoogleSignInButton';
import Header from '../../components/Header/Header';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSmallTextSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { fontSize } = useFontSize();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  // Set page title and scroll to top
  useEffect(() => {
    document.title = 'Stock Table - Log In';
    window.scrollTo(0, 0);
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors = {
      email: '',
      password: ''
    };

    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email address is required.';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    }

    setErrors(newErrors);

    // If no errors, proceed with login
    if (!newErrors.email && !newErrors.password) {
      setIsLoading(true);
      
      try {
        const response = await fetch('/api/v1/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Login the user using the auth context (include refresh token if provided)
          login(data.accessToken, data.user, data.refreshToken);
          // Redirect to the main app
          navigate('/daily');
        } else {
          // Handle login errors
          if (data.message && data.message.includes('Invalid credentials')) {
            setErrors(prev => ({
              ...prev,
              email: 'Invalid email or password.'
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              email: data.message || 'Login failed. Please try again.'
            }));
          }
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          email: 'Network error. Please check your connection and try again.'
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setIsGoogleLoading(true);
    
    try {
      const response = await fetch('/api/v1/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credential,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Login the user using the auth context
        login(data.accessToken, data.user, data.refreshToken);
        // Redirect to the main app
        navigate('/daily');
      } else {
        setErrors(prev => ({
          ...prev,
          email: data.message || 'Google sign-in failed. Please try again.'
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        email: 'Network error. Please check your connection and try again.'
      }));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setErrors(prev => ({
      ...prev,
      email: 'Google sign-in was cancelled or failed. Please try again.'
    }));
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      <Header showSignUp={true} showLogIn={false} />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className={`mt-6 text-center ${getPageTitleSizeClass(fontSize)} font-bold tracking-tight text-text-light-primary dark:text-text-primary`}>
              Sign in to your account
            </h2>
            <p className={`mt-2 text-center ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
              Welcome back to Stock Table.
            </p>
          </div>
          
          <div className="bg-surface-light dark:bg-background-card p-8 rounded-lg shadow-lg border border-border-light dark:border-border-color">
            <div className="mb-6 w-full">
              <GoogleSignInButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="signin_with"
                disabled={isGoogleLoading}
              />
            </div>
            
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border-light dark:border-border-color"></div>
                </div>
                <div className={`relative flex justify-center ${getSmallTextSizeClass(fontSize)}`}>
                  <span className="px-2 bg-surface-light dark:bg-background-card text-text-light-secondary dark:text-text-secondary">Or continue with email</span>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="mt-6 space-y-6" noValidate>
              <div>
                <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary`} htmlFor="email">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    autoComplete="email"
                    className={`w-full p-3 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      errors.email 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="email"
                    name="email"
                    required
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.email && (
                  <p className={`mt-1 ${getSmallTextSizeClass(fontSize)} text-red-600 dark:text-red-400`}>{errors.email}</p>
                )}
              </div>
              
              <div>
                <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary`} htmlFor="password">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    autoComplete="current-password"
                    className={`w-full p-3 pr-12 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      errors.password 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="password"
                    name="password"
                    required
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 px-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      // Eye with diagonal slash overlay
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {/* Base eye (same as show icon) */}
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                        {/* Diagonal slash overlay */}
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4L20 20"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className={`mt-1 ${getSmallTextSizeClass(fontSize)} text-red-600 dark:text-red-400`}>{errors.password}</p>
                )}
              </div>
              
              <div className="flex items-center justify-end">
                <div className={getSmallTextSizeClass(fontSize)}>
                  <Link
                    to="/forgot-password"
                    className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
              
              <div>
                <button
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm ${getTextSizeClass(fontSize)} font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          </div>
          
          <p className={`mt-10 text-center ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
            Don't have an account?{' '}
            <Link 
              to="/signup"
              className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
